/**
 * PATH: packages/pricelist/src/RowContextMenu.tsx
 * FILE_ID: PKG-PRICELIST-ROWMENU-001
 * PURPOSE: Excel-like right-click row menu for the pricelist grid. AG-Grid
 *          Community has no rich context menu (Enterprise-only), so the grid
 *          forwards its `cellContextMenu` event and the workbook renders this
 *          custom floating menu wired to the workbook hook's row ops.
 * WHY: Operators expect right-click row editing (insert above/below, duplicate,
 *      delete) like a spreadsheet; the per-row "×" alone wasn't discoverable.
 * FLOW: grid onCellContextMenu → workbook setRowMenu({x,y,rowId}) → this menu
 *       → hook insertRowAt / duplicateRow / removeRow
 * RELATED: LazyWorkbookGrid (event source), PricelistWorkbook (host),
 *          usePricelistWorkbook (row ops)
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { ArrowUp, ArrowDown, Copy, Trash2 } from 'lucide-react'

export interface ContextMenuItem {
  key: string
  label: string
  icon: typeof ArrowUp
  run: () => void
  danger?: boolean
}

export interface RowContextMenuProps {
  /** Viewport coordinates of the right-click (clientX/clientY). */
  x: number
  y: number
  onInsertAbove?: () => void
  onInsertBelow?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  onClose: () => void
  /** Custom items replace the default row actions (e.g. column-header menu). */
  items?: ContextMenuItem[]
  ariaLabel?: string
}

/**
 * Floating, viewport-clamped row menu. Closes on outside click, Escape, scroll,
 * or resize so it can never strand on screen. Pure presentation — every action
 * is injected by the host, which owns the section/row context.
 */
export default function RowContextMenu({
  x,
  y,
  onInsertAbove,
  onInsertBelow,
  onDuplicate,
  onDelete,
  onClose,
  items: customItems,
  ariaLabel = 'Row actions',
}: RowContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState({ left: x, top: y })

  // Clamp inside the viewport AFTER measuring (a right-click near the bottom/right
  // edge must flip the menu in, not overflow off-screen). Pre-paint so there's no
  // visible jump.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { offsetWidth: w, offsetHeight: h } = el
    const pad = 8
    const left = Math.min(x, window.innerWidth - w - pad)
    const top = Math.min(y, window.innerHeight - h - pad)
    setPos({ left: Math.max(pad, left), top: Math.max(pad, top) })
  }, [x, y])

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(true, onClose, [ref])
  useEffect(() => {
    const close = () => onClose()
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [onClose])

  const items: ContextMenuItem[] =
    customItems ??
    ([
      onInsertAbove && { key: 'above', label: 'Insert row above', icon: ArrowUp, run: onInsertAbove },
      onInsertBelow && { key: 'below', label: 'Insert row below', icon: ArrowDown, run: onInsertBelow },
      onDuplicate && { key: 'dup', label: 'Duplicate row', icon: Copy, run: onDuplicate },
      onDelete && { key: 'del', label: 'Delete row', icon: Trash2, run: onDelete, danger: true },
    ].filter(Boolean) as ContextMenuItem[])

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={ariaLabel}
      className="fixed z-[1000] min-w-[180px] overflow-hidden rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-800"
      style={{ left: pos.left, top: pos.top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((it) => {
        const Icon = it.icon
        return (
          <button
            key={it.key}
            type="button"
            role="menuitem"
            className={
              'flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors ' +
              (it.danger
                ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40'
                : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700/60')
            }
            onClick={() => {
              it.run()
              onClose()
            }}
          >
            <Icon className="h-3.5 w-3.5 flex-shrink-0" />
            {it.label}
          </button>
        )
      })}
    </div>
  )
}
